import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Bell, X, CheckCheck } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

const TITLES = {
  doc_expiry: "Scadenza documento",
  new_service: "Nuovo servizio assegnato",
  exchange_assigned: "Nuovo servizio affidato",
  service_modified: "Servizio modificato",
  service_modified_after_view: "Servizio modificato",
  service_cancelled: "Servizio cancellato",
  service_reminder: "Promemoria servizio",
  shift_changed: "Turno aggiornato",
  cedolino_ready: "Riepilogo mensile disponibile",
  driver_assignment: "Servizio assegnato",
};
const clean = (m) => (m || "").replace(/^\[NOTIFICA AUTISTA\]\s*/, "").trim();
const fmt = (iso) => { try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

// Ogni evento genera un record per canale (WhatsApp/Email): raggruppa per idempotency_key.
const group = (rows) => {
  const map = new Map();
  for (const n of rows) {
    const k = n.idempotency_key || n.id;
    const g = map.get(k) || { key: k, ids: [], type: n.type, booking_number: n.booking_number, message: "", created_at: n.created_at, read: true };
    g.ids.push(n.id);
    if ((n.message || "").length > g.message.length) g.message = n.message;
    if (!n.read) g.read = false;
    map.set(k, g);
  }
  return [...map.values()].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
};

export const DriverNotificationsBell = () => {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState(null);
  const load = useCallback(() => api.get("/notifications").then(r => setRows(r.data || [])).catch(() => {}), []);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);
  const groups = useMemo(() => group(rows), [rows]);
  const unread = groups.filter(g => !g.read).length;

  const markRead = async (g) => {
    if (g.read) return;
    try {
      await Promise.all(g.ids.map(id => api.patch(`/notifications/${id}/read`)));
      setRows(rs => rs.map(n => (g.ids.includes(n.id) ? { ...n, read: true } : n)));
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const openItem = (g) => { setSel(g.key === sel ? null : g.key); markRead(g); };
  const markAll = () => Promise.all(groups.filter(g => !g.read).map(markRead));

  return (
    <>
      <button type="button" onClick={() => { setOpen(true); load(); }} className="relative w-11 h-11 -m-3 flex items-center justify-center text-slate-400 hover:text-white touch-manipulation" title="Notifiche" data-testid="driver-notif-bell">
        <Bell size={20}/>
        {unread > 0 && <span className="absolute top-1 right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="driver-notif-count">{unread > 99 ? "99+" : unread}</span>}
      </button>
      {open && createPortal(
        <div className="fixed inset-0 z-[60]" data-testid="driver-notif-panel">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-0 h-full w-full sm:w-[420px] bg-[#0C121E] border-l border-slate-800 shadow-2xl flex flex-col pt-safe">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800">
              <div className="text-white font-bold flex items-center gap-2"><Bell size={18} className="text-amber-400"/>Notifiche {unread > 0 && <span className="text-xs text-rose-300">({unread} non lette)</span>}</div>
              <div className="flex items-center gap-2">
                {unread > 0 && <button type="button" onClick={markAll} className="text-xs text-amber-300 inline-flex items-center gap-1 px-2 py-1" data-testid="driver-notif-mark-all"><CheckCheck size={14}/>Segna tutte lette</button>}
                <button type="button" onClick={() => setOpen(false)} className="w-10 h-10 flex items-center justify-center text-slate-400" data-testid="driver-notif-close"><X size={18}/></button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {groups.length === 0 && <div className="text-center text-slate-500 text-sm py-12" data-testid="driver-notif-empty">Non hai nuove notifiche</div>}
              {groups.map(g => (
                <button type="button" key={g.key} onClick={() => openItem(g)} data-testid={`driver-notif-item-${g.key}`}
                  className={`w-full text-left rounded-xl border p-3 transition-colors ${g.read ? "border-slate-800 bg-slate-950/40" : "border-amber-500/40 bg-amber-500/[0.06]"}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className={`text-sm ${g.read ? "text-slate-300" : "text-white font-semibold"}`}>
                      {!g.read && <span className="inline-block w-2 h-2 rounded-full bg-amber-400 mr-2 align-middle" data-testid="driver-notif-unread-dot"/>}
                      {TITLES[g.type] || "Notifica"}{g.booking_number ? ` · ${g.booking_number}` : ""}
                    </div>
                    <div className="text-[10px] text-slate-500 whitespace-nowrap">{fmt(g.created_at)}</div>
                  </div>
                  <div className={`text-xs text-slate-300 mt-1.5 break-words ${sel === g.key ? "whitespace-pre-wrap" : "line-clamp-2"}`}>{clean(g.message) || "—"}</div>
                </button>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default DriverNotificationsBell;
